import "./ErrorLogs.css";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useApi from "../../../helpers/apiHelper/requestHelper";
import TablePagination from "../../sharedPages/CommonComponents/NewPagination/NewPagination";
import SearchBar from "../../sharedPages/CommonComponents/SearchBar/SearchBar";

const PAGE_SIZE = 50;

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY, HH:mm:ss") : "—";
};

const titleCase = (value) => {
  if (!value) return "—";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const severityClass = (severity) => {
  const key = String(severity || "").toLowerCase();
  if (key === "critical" || key === "error") return "is-critical";
  if (key === "warning") return "is-warning";
  return "is-info";
};

const ErrorLogsList = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchLogs = async (pageNumber = page) => {
    setLoading(true);
    try {
      const response = await api(
        "get",
        `authorizeSuperAdmin/error-logs?page=${pageNumber}&limit=${PAGE_SIZE}`
      );
      if (response?.status) {
        setItems(Array.isArray(response?.data?.items) ? response.data.items : []);
        setTotal(Number(response?.data?.total) || 0);
        setCounts(response?.data?.counts || null);
      } else {
        setItems([]);
        setTotal(0);
        setCounts(null);
      }
    } catch (error) {
      setItems([]);
      setTotal(0);
      setCounts(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      if (severityFilter !== "all" && item.severity !== severityFilter) {
        return false;
      }
      if (moduleFilter !== "all" && item.module !== moduleFilter) {
        return false;
      }
      if (statusFilter !== "all" && item.status !== statusFilter) {
        return false;
      }
      if (!query) return true;
      const haystack = [
        item.message,
        item.userEmail,
        item.user?.email,
        item.leader?.email,
        item.symbol,
        item.module,
        item.action,
        item.exchange,
        item.details?.flow,
        item.leaderOrderId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, searchQuery, severityFilter, moduleFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const moduleOptions = useMemo(() => {
    const fromCounts = Object.keys(counts?.byModule || {});
    if (fromCounts.length) return fromCounts;
    return [...new Set(items.map((item) => item.module).filter(Boolean))];
  }, [counts, items]);

  return (
    <div className="errorLogsPage">
      <div className="errorLogsHeader">
        <div>
          <h2 className="errorLogsTitle">Error Logs</h2>
          <p className="errorLogsSubtitle">
            Trading miss / warning events across futures and spot
          </p>
        </div>
        <button
          type="button"
          className="errorLogsRefreshBtn"
          onClick={fetchLogs}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {counts && (
        <div className="errorLogsStats">
          <div className="errorLogsStatCard">
            <span>Open</span>
            <strong>{counts.open ?? 0}</strong>
          </div>
          <div className="errorLogsStatCard is-critical">
            <span>Critical</span>
            <strong>{counts.critical ?? 0}</strong>
          </div>
          <div className="errorLogsStatCard">
            <span>CM</span>
            <strong>{counts.byActorRole?.cm ?? 0}</strong>
          </div>
          <div className="errorLogsStatCard">
            <span>CL</span>
            <strong>{counts.byActorRole?.cl ?? 0}</strong>
          </div>
          <div className="errorLogsStatCard">
            <span>Futures</span>
            <strong>{counts.byModule?.trading_futures ?? 0}</strong>
          </div>
          <div className="errorLogsStatCard">
            <span>Spot</span>
            <strong>{counts.byModule?.trading_spot ?? 0}</strong>
          </div>
        </div>
      )}

      <div className="errorLogsToolbar">
        <div className="errorLogsFilters">
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <option value="all">All severity</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
            <option value="info">Info</option>
          </select>
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
          >
            <option value="all">All modules</option>
            {moduleOptions.map((module) => (
              <option key={module} value={module}>
                {titleCase(module)}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All status</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="errorLogsSearch">
          <SearchBar
            value={searchQuery}
            searchValue="Search email, symbol, message…"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="errorLogsTableScroll">
        <table className="errorLogsTable">
          <colgroup>
            <col className="col-time" />
            <col className="col-severity" />
            <col className="col-module" />
            <col className="col-message" />
            <col className="col-member" />
            <col className="col-leader" />
            <col className="col-symbol" />
            <col className="col-count" />
          </colgroup>
          <thead>
            <tr>
              <th>Time</th>
              <th>Severity</th>
              <th>Module</th>
              <th>Message</th>
              <th>Member</th>
              <th>Leader</th>
              <th>Symbol</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr className="errorLogsEmpty">
                <td colSpan={8}>Loading error logs…</td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr className="errorLogsEmpty">
                <td colSpan={8}>No error logs found</td>
              </tr>
            ) : (
              filteredItems.map((item) => (
                <tr
                  key={item._id}
                  onClick={() =>
                    navigate(`/authorizeSuperAdmin/error-logs/${item._id}`, {
                      state: { log: item },
                    })
                  }
                >
                  <td>
                    <span className="errorLogsCell" title={formatDateTime(item.lastOccurredAt || item.createdAt)}>
                      {formatDateTime(item.lastOccurredAt || item.createdAt)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`errorLogsBadge ${severityClass(
                        item.severity
                      )}`}
                    >
                      {titleCase(item.severity)}
                    </span>
                  </td>
                  <td>
                    <span className="errorLogsCell" title={titleCase(item.module)}>
                      {titleCase(item.module)}
                    </span>
                  </td>
                  <td>
                    <span className="errorLogsCell" title={item.message || ""}>
                      {item.message || "—"}
                    </span>
                  </td>
                  <td>
                    <span
                      className="errorLogsCell"
                      title={item.userEmail || item.user?.email || ""}
                    >
                      {item.userEmail || item.user?.email || "—"}
                    </span>
                  </td>
                  <td>
                    <span className="errorLogsCell" title={item.leader?.email || ""}>
                      {item.leader?.email || "—"}
                    </span>
                  </td>
                  <td>
                    <span className="errorLogsCell" title={item.symbol || ""}>
                      {item.symbol || "—"}
                    </span>
                  </td>
                  <td>
                    <span className="errorLogsCell">
                      {item.occurrenceCount ?? 1}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="errorLogsPagination">
          <TablePagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};

export default ErrorLogsList;
